import { combineReducers, configureStore } from '@reduxjs/toolkit';

import { accountApi } from '@api/account/account.api';
import { applicationApi } from '@api/application/application.api';
import { authApi } from '@api/auth/auth.api';
import { billingApi } from '@api/billing/billing.api';
import { connectionApi } from '@api/connection/connection.api';
import { connectorApi } from '@api/connector/connector.api';
import { consumerApi } from '@api/consumer/consumer.api';
import { credentialApi } from '@api/credential/credential.api';
import { integrationApi } from '@api/integration/integration.api';
import { logsApplicationApi } from '@api/logs/logs-application.api';
import { logsConsumerApi } from '@api/logs/logs-consumer.api';
import { membersApi } from '@api/members/members.api';
import { mfaApi } from '@api/mfa/mfa.api';
import { statsApi } from '@api/stats/stats.api';
import { unifyApi } from '@api/unify-api/unify.api';
import { userApi } from '@api/user/user.api';
import { webhookApi } from '@api/webhook/webhook.api';
import { commonSlice } from '@store/common/common.slice';
import { logsSlice } from '@store/logs/logs.slice';

import { accountsSlice } from './accounts/accounts.slice';
import { applicationsSlice } from './applications/applications.slice';
import { authSlice } from './auth/auth.slice';
import { connectionsSlice } from './connections/connections.slice';
import { connectorsSlice } from './connectors/connectors.slice';
import { consumersSlice } from './consumers/consumers.slice';
import { credentialsSlice } from './credentials/credentials.slice';
import { integrationsSlice } from './integrations/integrations.slice';
import { membersSlice } from './members/members.slice';
import { routeSlice } from './route/route.slice';
import { subscriptionSlice } from './subscription/subscription.slice';
import { unifyApiSlice } from './unify-api/unify-api.slice';
import { userSlice } from './user/user.slice';
import { webhooksSlice } from './webhooks/webhooks.slice';

const combinedReducers: any = combineReducers({
  // Primary API
  [accountApi.reducerPath]: accountApi.reducer,
  [applicationApi.reducerPath]: applicationApi.reducer,
  [authApi.reducerPath]: authApi.reducer,
  [billingApi.reducerPath]: billingApi.reducer,
  [connectionApi.reducerPath]: connectionApi.reducer,
  [connectorApi.reducerPath]: connectorApi.reducer,
  [consumerApi.reducerPath]: consumerApi.reducer,
  [credentialApi.reducerPath]: credentialApi.reducer,
  [integrationApi.reducerPath]: integrationApi.reducer,
  [membersApi.reducerPath]: membersApi.reducer,
  [mfaApi.reducerPath]: mfaApi.reducer,
  [logsConsumerApi.reducerPath]: logsConsumerApi.reducer,
  [logsApplicationApi.reducerPath]: logsApplicationApi.reducer,
  [statsApi.reducerPath]: statsApi.reducer,
  [unifyApi.reducerPath]: unifyApi.reducer,
  [userApi.reducerPath]: userApi.reducer,
  [webhookApi.reducerPath]: webhookApi.reducer,

  // Application Store
  [accountsSlice.name]: accountsSlice.reducer,
  [applicationsSlice.name]: applicationsSlice.reducer,
  [authSlice.name]: authSlice.reducer,
  [subscriptionSlice.name]: subscriptionSlice.reducer,
  [commonSlice.name]: commonSlice.reducer,
  [connectionsSlice.name]: connectionsSlice.reducer,
  [connectorsSlice.name]: connectorsSlice.reducer,
  [consumersSlice.name]: consumersSlice.reducer,
  [credentialsSlice.name]: credentialsSlice.reducer,
  [integrationsSlice.name]: integrationsSlice.reducer,
  [membersSlice.name]: membersSlice.reducer,
  [logsSlice.name]: logsSlice.reducer,
  [routeSlice.name]: routeSlice.reducer,
  [unifyApiSlice.name]: unifyApiSlice.reducer,
  [userSlice.name]: userSlice.reducer,
  [webhooksSlice.name]: webhooksSlice.reducer,
});

export const store: any = configureStore({
  reducer: combinedReducers,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware().concat([
      accountApi.middleware,
      applicationApi.middleware,
      authApi.middleware,
      billingApi.middleware,
      connectionApi.middleware,
      connectorApi.middleware,
      consumerApi.middleware,
      credentialApi.middleware,
      integrationApi.middleware,
      membersApi.middleware,
      mfaApi.middleware,
      logsConsumerApi.middleware,
      logsApplicationApi.middleware,
      statsApi.middleware,
      unifyApi.middleware,
      userApi.middleware,
      webhookApi.middleware,
    ]),
  devTools: import.meta.env.DEV,
});

export type RootStateType = any;
export type AppDispatchType = typeof store.dispatch;
